import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Mail, Calendar, Package } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PurchaseDetails {
  email: string;
  box_slots_purchased: number;
  amount_total: number;
  currency: string;
}

const StorageOnboardingBridge = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [purchase, setPurchase] = useState<PurchaseDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const verifyPurchase = async () => {
      if (!sessionId) {
        setError("No checkout session found");
        setLoading(false);
        return;
      }

      try {
        const { data, error: verifyError } = await supabase.functions.invoke(
          'verify-purchase-session',
          { body: { session_id: sessionId } }
        );

        if (verifyError) throw verifyError;
        if (!data.success) throw new Error('Payment verification failed');

        setPurchase(data);
      } catch (err) {
        console.error('Verification error:', err);
        setError('Failed to verify payment');
      } finally {
        setLoading(false);
      }
    };

    verifyPurchase();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !purchase) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Payment Verification Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => window.location.href = '/'}>
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const monthlyCost = (purchase.box_slots_purchased * 9.99).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold text-primary">Endless Storage</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
            <CheckCircle2 className="h-10 w-10 text-primary animate-scale-in" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Payment Successful!</h1>
          <p className="text-xl text-muted-foreground">
            Welcome to Endless Storage
          </p>
        </div>

        <Card className="mb-8 shadow-elegant">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between p-6 bg-primary/5 rounded-lg">
              <div className="flex items-center gap-4">
                <Package className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{purchase.box_slots_purchased} Box Slots</p>
                  <p className="text-muted-foreground">Storage subscription</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-primary">${monthlyCost}</p>
                <p className="text-sm text-muted-foreground">per month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Mail className="h-6 w-6 text-primary" />
              <CardTitle>Check Your Email</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-lg">
              We sent a magic link to <strong>{purchase.email}</strong>
            </p>
            
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="font-semibold mb-2">📧 Email Subject:</p>
              <p className="text-sm text-muted-foreground">
                "Welcome to Endless Storage - Set Up Your {purchase.box_slots_purchased} Boxes"
              </p>
            </div>

            <ol className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="font-bold text-primary">1.</span>
                <span>Click the magic link in your email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-primary">2.</span>
                <span>We'll guide you through creating your boxes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-primary">3.</span>
                <span>Schedule your first FREE pickup</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>What Happens Next</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <TimelineStep
                icon={<CheckCircle2 className="h-5 w-5" />}
                status="Complete"
                title="Payment Received"
                color="text-primary"
              />
              <TimelineStep
                icon={<Package className="h-5 w-5" />}
                status="Next"
                title="Set Up Your Boxes"
                subtitle="After clicking the email link"
                color="text-orange-500"
              />
              <TimelineStep
                icon={<Calendar className="h-5 w-5" />}
                status="Upcoming"
                title="Schedule Pickup"
                subtitle="Choose date & time"
                color="text-muted-foreground"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const TimelineStep = ({ icon, status, title, subtitle, color }: any) => (
  <div className="flex items-start gap-4">
    <div className={`${color} mt-1`}>{icon}</div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <p className="font-semibold">{title}</p>
        <span className={`text-xs px-2 py-1 rounded-full bg-muted ${color}`}>
          {status}
        </span>
      </div>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  </div>
);

export default StorageOnboardingBridge;